import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Header } from '../common/header/Header';
import { Sidebar } from '../common/sidebar/Sidebar';

import { Achievements } from '../pages/achievements/Achievements';
import { BusinessDetails } from '../pages/businessDetails/BusinessDetails';
import { Businesses } from '../pages/businesses/Businesses';
import { Categories } from '../pages/categories/Categories';
import { Notification } from '../pages/notification/Notification';
import { NotificationWithFilters } from '../pages/notification/NotificationWithFilters';
import { PageNotFound } from '../pages/pageNotFound/PageNotFound';
import { ReportsPage } from '../pages/reports/ReportsPage';
import { UserDetails } from '../pages/userDetails/UserDetails';
import { Users } from '../pages/users/Users';
import { Reviews } from '../pages/reviews/Reviews';
import { Feedback } from '../pages/feedback/Feedback';
import { Leaderboard } from '../pages/leaderboard/Leaderboard';
import { SmsWithFilters } from '../pages/sms/SmsWithFilters';
import { WhatsAppBroadcast } from '../pages/whatsapp/WhatsAppBroadcast';
import { WhatsAppMessages } from '../pages/whatsapp/WhatsAppMessages';
import { ReferralActivity } from '../pages/referrals/ReferralActivity';
import { UserActivity } from '../pages/userActivity/UserActivity';
import { BusinessRecommendationActivity } from '../pages/businessRecommendationActivity/BusinessRecommendationActivity';
import { AppConfig } from '../pages/appConfig/AppConfig';
import { RateLimits } from '../pages/rateLimits/RateLimits';
import { CountryBlocking } from '../pages/countryBlocking/CountryBlocking';
import { UserBans } from '../pages/userBans/UserBans';
import { crmDashboardRoute } from '../pages/crm/CrmRoutes';

const HEADER_HEIGHT = '4rem';
const SIDEBAR_WIDTH = '300px';

/** The only scroll container: pages scroll here while header + sidebar stay put. */
const Main = styled('main')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflowY: 'auto',
  padding: '2rem',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem',
  },
}));

// Signed-out visitors never reach this: AppRouter wraps it in RequireAuth.
export const DashboardRoutes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarToggle, setSidebarToggle] = useState(!isMobile);

  // Overlay closed by default on mobile; rail open by default on desktop.
  useEffect(() => {
    setSidebarToggle(!isMobile);
  }, [isMobile]);

  const width = sidebarToggle ? SIDEBAR_WIDTH : 0;

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: 'background.default',
    }}>
      {/* Full-viewport-height rail on desktop; floating overlay on mobile. */}
      <Sidebar
        width={width}
        overlay={isMobile}
        onClose={isMobile ? () => setSidebarToggle(false) : undefined}
      />

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Header headerHeight={HEADER_HEIGHT} setToggle={setSidebarToggle} toggle={sidebarToggle} />

        <Main>
          <Routes>
            <Route path="/" element={<PageNotFound />} />
            <Route path="/users/:userId" element={<UserDetails />} />
            <Route path="/users" element={<Users />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/businesses" element={<Businesses />} />
            <Route path="/businesses/:placeId" element={<BusinessDetails />} />
            <Route path="/notification" element={<Notification />} />
            <Route path="/notification-new" element={<NotificationWithFilters />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/point-history" element={<Leaderboard />} />
            <Route path="/sms" element={<SmsWithFilters />} />
            <Route path="/whatsapp" element={<WhatsAppBroadcast />} />
            <Route path="/whatsapp-messages" element={<WhatsAppMessages />} />
            <Route path="/referrals" element={<ReferralActivity />} />
            <Route path="/user-activity" element={<UserActivity />} />
            <Route path="/business-recommendation-activity" element={<BusinessRecommendationActivity />} />
            <Route path="/app-config" element={<AppConfig />} />
            <Route path="/rate-limits" element={<RateLimits />} />
            <Route path="/country-blocking" element={<CountryBlocking />} />
            <Route path="/user-bans" element={<UserBans />} />
            {/* /crm/*: its own table is CrmRoutes. */}
            {crmDashboardRoute()}
            <Route path="*" element={<Navigate to="/" replace={true} />} />
          </Routes>
        </Main>
      </Box>
    </Box>
  );
};
